import { useMutation, useQuery } from '@apollo/client/react';
import React, { ReactNode } from 'react';

import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import Clapping from '../../assets/icons/Clapping.svg';
import Fire from '../../assets/icons/Fire.svg';
import Grimacing from '../../assets/icons/Grimacing.svg';
import Laugh from '../../assets/icons/Laugh.svg';
import Smile from '../../assets/icons/Smile.svg';
import ThumbsUp from '../../assets/icons/ThumbsUp.svg';
import {
  AddReactionDocument,
  MessageDetailsFragment,
  ReactionFragment,
  ReactionKind,
  RemoveReactionDocument,
  SelfDocument,
} from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import {
  AQUAMARINE,
  COOL_GRAY800,
  GRAY100,
  GRAY200,
  GREEN050,
  WHITE,
} from '../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { IconButton } from '../reusable/Button/IconButton';
import Text, { TextKind } from '../reusable/Text';
import { useChatContext } from './Context';
import { MessageWithGroupingMetadata } from './util';

const REACTION_ORDER = [
  ReactionKind.Thumbsup,
  ReactionKind.Smile,
  ReactionKind.Laugh,
  ReactionKind.Clap,
  ReactionKind.Fire,
  ReactionKind.Grimacing,
];
const kindToEmoji = (kind: ReactionKind) => {
  const supportedEmojis: Omit<
    Record<ReactionKind, ReactNode>,
    'rating_negative' | 'rating_positive'
  > = {
    thumbsup: <img src={ThumbsUp} alt="Thumbs up emoji" />,
    smile: <img src={Smile} alt="Smile emoji" />,
    laugh: <img src={Laugh} alt="Laugh emoji" />,
    clap: <img src={Clapping} alt="Clapping emoji" />,
    fire: <img src={Fire} alt="Fire emoji" />,
    grimacing: <img src={Grimacing} alt="Grimacing emoji" />,
  };
  return supportedEmojis[kind as keyof typeof supportedEmojis];
};

export const TapBackFlyout = styled.div<{ $isSentMessage: boolean }>`
  display: none;
  position: absolute;
  bottom: calc(100% + 10px);
  z-index: 2;
  height: 40px;
  align-items: center;
  border-radius: 40px;
  background: ${WHITE};
  padding: 4px;
  box-shadow: 0px 4px 4px 0px #0000001a;
  border: 1px solid ${GRAY200};

  &:hover {
    display: flex;
  }

  &:after {
    /* Additional hover area to accomodate inaccurate mouse movement */
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: calc(100% * 2);
    z-index: -1;
  }

  ${({ $isSentMessage }) => {
    if ($isSentMessage) {
      return css`
        right: 0px;
      `;
    }
    return css`
      left: -3px;
    `;
  }}
`;

export const useReaction = (
  message: MessageDetailsFragment,
  selfId?: string,
  trackTapback = true,
) => {
  const { track } = useTracking();
  const { chatId } = useChatContext();

  const [addReaction] = useMutation(AddReactionDocument);
  const [removeReaction] = useMutation(RemoveReactionDocument);

  const toggleReaction = (kind: ReactionKind) => {
    const trackingInfo = {
      event_category: EventCategories.Chat,
      chat_id: chatId,
      message_id: message.id,
      tapback_kind: kind,
    };
    const thisReactionFromCoach = message.reactions.find(
      (reaction) => reaction.kind === kind && reaction.user.id === selfId,
    );

    // If attempting to mutate an optimistic response, do nothing
    // This will eat clicks from the user on slow networks but
    // is necessary to prevent errors or duplicate reactions
    if (
      thisReactionFromCoach &&
      thisReactionFromCoach.id.startsWith('optimistic_')
    ) {
      return;
    }

    if (thisReactionFromCoach) {
      removeReaction({
        variables: { reactionId: thisReactionFromCoach.id },
        optimisticResponse: {
          removeReaction: {
            __typename: 'Message',
            id: message.id,
            reactions: message.reactions.filter(
              (reaction) => reaction.id !== thisReactionFromCoach.id,
            ),
          },
        },
      });
      if (trackTapback) {
        track(EventNames.TapBack, {
          ...trackingInfo,
          event_subcategory: EventSubCategories.Remove,
        });
      }
      return;
    }

    addReaction({
      variables: {
        messageId: message.id,
        kind,
      },
      optimisticResponse: selfId
        ? {
            addReaction: {
              __typename: 'Message',
              id: message.id,
              reactions: [
                ...message.reactions,
                {
                  __typename: 'Reaction',
                  id: `optimistic_${uuid()}`,
                  kind,
                  user: {
                    __typename: 'User',
                    id: selfId,
                  },
                },
              ],
            },
          }
        : undefined,
    });
    if (trackTapback) {
      track(EventNames.TapBack, {
        ...trackingInfo,
        event_subcategory: EventSubCategories.Add,
      });
    }
  };
  return { toggleReaction };
};

export const TapBack: React.FC<{
  isSentMessage: boolean;
  message: MessageWithGroupingMetadata;
  testId?: string;
}> = ({ isSentMessage, message, testId }) => {
  const { data: selfQueryResponse } = useQuery(SelfDocument, {
    fetchPolicy: 'cache-first',
  });

  const { toggleReaction } = useReaction(message, selfQueryResponse?.self?.id);

  return (
    <TapBackFlyout
      $isSentMessage={isSentMessage}
      data-testid={`tapback-container${testId}`}
      className="tapback-container"
    >
      {REACTION_ORDER.map((kind) => (
        <IconButton
          icon={kindToEmoji(kind)}
          testId={`tapback-button-${kind}`}
          onClick={() => toggleReaction(kind)}
          key={kind}
        />
      ))}
    </TapBackFlyout>
  );
};

interface ReactionCountByKind {
  count: number;
  kind: ReactionKind;
  isSent: boolean;
}

const getSortedReactionCountsByKind = (
  reactions: ReactionFragment[],
  selfId?: string,
) =>
  reactions
    .reduce((countsByKind, reaction) => {
      const countByKind = countsByKind.find(
        (count) => count.kind === reaction.kind,
      );
      if (countByKind) {
        countByKind.count += 1;
        if (countByKind.isSent === false && reaction.user.id === selfId) {
          countByKind.isSent = true;
        }
      } else {
        countsByKind.push({
          count: 1,
          kind: reaction.kind,
          isSent: reaction.user.id === selfId,
        });
      }
      return countsByKind;
    }, [] as ReactionCountByKind[])
    .sort((a, b) =>
      REACTION_ORDER.indexOf(a.kind) >= REACTION_ORDER.indexOf(b.kind) ? -1 : 1,
    );

const ReactionsContainer = styled.div<{
  $isSentMessage: boolean;
}>`
  display: flex;
  gap: 4px;
  margin: 4px 16px 4px 56px;

  ${({ $isSentMessage }) =>
    $isSentMessage
      ? css`
          justify-content: flex-end;
        `
      : ''}
`;

const EmojiPill = styled.button<{ $isSent: boolean }>`
  width: fit-content;
  min-width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border: 1px solid;
  border-radius: 24px;
  padding: 4px 8px;
  cursor: pointer;

  ${({ $isSent }) =>
    $isSent
      ? css`
          border-color: ${AQUAMARINE};
          background-color: ${GREEN050};

          &:hover {
            border-color: ${COOL_GRAY800};
            background-color: ${WHITE};
          }
        `
      : css`
          border-color: ${GRAY100};
          background-color: ${GRAY100};

          &:hover {
            border-color: ${AQUAMARINE};
            background-color: ${WHITE};
          }
        `}
  &
    img {
    width: 16px;
  }
`;

export const Reactions: React.FC<{
  message: MessageWithGroupingMetadata;
  isSentMessage: boolean;
}> = ({ message, isSentMessage }) => {
  const { data: selfQueryResponse } = useQuery(SelfDocument, {
    fetchPolicy: 'cache-first',
  });

  const { toggleReaction } = useReaction(message, selfQueryResponse?.self?.id);

  const countsByKind = getSortedReactionCountsByKind(
    message.reactions,
    selfQueryResponse?.self?.id,
  );

  if (!countsByKind.length) {
    return null;
  }

  return (
    <ReactionsContainer $isSentMessage={isSentMessage}>
      {countsByKind.map(({ count, kind, isSent }) => (
        <EmojiPill
          data-testid={`reaction-button-${kind}`}
          $isSent={isSent}
          onClick={() => toggleReaction(kind)}
          key={kind}
        >
          {kindToEmoji(kind)}
          {count > 1 && (
            <Text kind={TextKind.LabelM} style={{ marginTop: '2px' }}>
              {count}
            </Text>
          )}
        </EmojiPill>
      ))}
    </ReactionsContainer>
  );
};
